const RawExternalDataPGAdapter = require('../../common/raw-external-pg-adapter')
const PGAdapter = require('../../common/pg-adapter')
const { transformWarehouseData } = require('../tools/transform-rms-stock')
const { generateWarehouseStock } = require('../tools/generate-warehouse-stock')

const columnPreparers = {
  created_at: (key, value) => {
    return [{
      statement: `"${key}">=%INDEX%::date`,
      value
    }]
  }
}
class RMSStockToAvocadoPGAdapter {
  constructor (api, pgConnection, logger, username) {
    this.api = api
    this.pgConnection = pgConnection
    this.logger = logger
    this.username = username

    this.rawDataAdapter = new RawExternalDataPGAdapter({
      pgConnection,
      username,
      logger
    })

    // Adapter for data_ext_stockevent table
    this.stockEventAdapter = new PGAdapter(
      pgConnection,
      'avocado.data_ext_stockevent',
      username,
      ['id', 'location_id', 'organisation_id', 'sku', 'date', 'closing', 'source', 'created_at', 'updated_at', 'created_by', 'updated_by'],
      'id',
      columnPreparers,
      logger
    )
  }

  /**
   * Sync RMS raw stock data into raw.external_stock_data table
   * @param {String} organisationId - Organisation ID
   * @param {Object} rmsStockData - Raw RMS stock data
   * @param {Object} options - Options including client for transaction support
   */
  async syncRMSRawData (organisationId, rmsStockData, options = {}) {
    try {
      // Store in raw table - just add fetch timestamp and request params
      const dataToStore = rmsStockData.items.map(item => ({
        organisation_id: organisationId,
        data: item
      }))

      const savedRecords = await this.rawDataAdapter.upsertBatchWithJsonbPaths(dataToStore, {
        conflictIdColumns: ['organisation_id'],
        conflictJsonbPaths: [
          { column: 'data', key: 'sapCode' },
          { column: 'data', key: 'program' },
          { column: 'data', key: 'valuationArea' },
          { column: 'data', key: 'ingestDate' }
        ],
        batchSize: 2000,
        client: options.client
      })

      this.logger.info('DEBUG: Raw data upsert completed', {
        savedRecordsCount: savedRecords.length,
        sampleSavedRecord: savedRecords[0]
      })

      const savedRecordIds = savedRecords.map(record => record.id)

      return {
        success: true,
        total: savedRecords.length,
        savedRecordIds
      }
    } catch (error) {
      this.logger.error('Failed to fetch and store RMS data', {
        error: error.message,
        organisationId
      })
      throw error
    }
  }

  /**
   * Transform RMS stock data to data_ext_stockevent format
   */
  async transformDailyRawDocs (organisationId, date, options = {}) {
    if (!organisationId) {
      throw new Error('organisationId is required')
    }
    // Get all raw records for the organisation for the given date
    const allRecordsFromToday = await this.rawDataAdapter.getByOrganisation(organisationId, date, options)

    this.logger.info(`Found ${allRecordsFromToday.length} records for ${date}`)

    if (allRecordsFromToday.length === 0) {
      return {
        success: true,
        totalRecords: allRecordsFromToday.length
      }
    }

    const transformedDocs = transformWarehouseData(organisationId, allRecordsFromToday, this.username)
    return transformedDocs
  }

  /**
   * Sync RMS transformed stock data into data_ext_stockevent table
   * @param {String} organisationId - Organisation ID
   * @param {Date} date - Date to filter raw records
   * @param {Object} options - Optional parameters including client for transactions
   */
  async syncTransformedStockData (organisationId, date = new Date(), options = {}) {
    const formattedDate = date.toISOString().split('T')[0]
    if (!organisationId) {
      throw new Error('organisationId is required')
    }
    try {
      const transformedRecords = await this.transformDailyRawDocs(organisationId, formattedDate, options)
      if (transformedRecords.length === 0) {
        return this.logger.info('No transformed records to sync', {
          organisationId,
          date
        })
      }

      const savedStockResponse = await this.stockEventAdapter.upsert(transformedRecords, {
        conflictKeys: ['sku', 'date', 'source'],
        client: options.client
      })
      this.logger.info('Successfully synced transformed stock data', {
        organisationId,
        date,
        totalRecords: transformedRecords.length,
        upsertedRecords: savedStockResponse.length
      })
      return savedStockResponse
    } catch (error) {
      this.logger.error('Failed to sync transformed stock data', {
        error: error.message,
        organisationId,
        date
      })
      throw error
    }
  }

  async stockEvents ({organisationId, branchCode = null}) {
    if (!organisationId) {
      throw new Error('organisationId is required')
    }
    const params = [organisationId]
    let query = `
      SELECT DISTINCT CAST(date AS DATE) AS date
      FROM avocado.data_ext_stockevent
      WHERE organisation_id = $1
    `
    if (branchCode) {
      query += ` AND source = $2`
      params.push(branchCode)
    }

    query += ` ORDER BY date DESC`

    const { rows } = await this.pgConnection.query(query, params)

    const formattedResults = rows.map(row => ({
      createdAt: row.date.toISOString().split('T')[0],
      status: 'completed'
    }))

    return formattedResults
  }

  async getWarehouseStocks ({ organisationId, date, branchCode = null }) {
    if (!organisationId) {
      throw new Error('organisationId is required')
    }

    const queryFilter = {
      organisation_id: organisationId,
      created_at: date
    }

    if (branchCode !== 'null' && branchCode !== null) {
      queryFilter.source = branchCode
    }

    const { results } = await this.stockEventAdapter.getList({ filter: queryFilter, limit: Number.MAX_SAFE_INTEGER })
    return generateWarehouseStock(this.api, organisationId, results, branchCode)
  }
}

module.exports = RMSStockToAvocadoPGAdapter
